// All 7 product modules with full content
export const MODULES = [
  {
    slug: "turnaround-control",
    name: "Turnaround Control Tower",
    brand: "GroundShield",
    icon: "Plane",
    badge: "LIVE OPS",
    tagline: "Every minute on stand costs money. See every second.",
    problem: "Ground handlers operate blind. Supervisors walk the apron with radios, clipboards and gut instinct. One delayed fuel truck cascades into a 40-minute delay. Nobody sees it coming.",
    solution: "A real-time digital control tower for every aircraft on stand. Every task tracked, every vendor accountable, every delay flagged before it happens — across all stations from one screen.",
    steps: [
      { n: "01", title: "Flight auto-populates", body: "Arriving flights load automatically with stand assignment, task checklist and vendor schedule." },
      { n: "02", title: "Live task board", body: "Each turnaround task (fueling, catering, cleaning, boarding) shows live status updated by ramp staff mobile devices." },
      { n: "03", title: "Exception alerts", body: "Any task running late triggers an instant alert to the supervisor. AI predicts if the departure will be delayed based on current progress." },
    ],
    roi: [
      { metric: "12 min", label: "Avg delay reduction per turnaround" },
      { metric: "94%", label: "On-time departure rate" },
      { metric: "3×", label: "Faster exception resolution" },
    ],
    appLink: "/turnaround/board",
    color: "orange",
  },
  {
    slug: "ramp-app",
    name: "Ramp Mobile App",
    brand: "GroundShield",
    icon: "Smartphone",
    badge: "MOBILE PWA",
    tagline: "Put every ramp agent on the same page. Literally.",
    problem: "Ramp staff receive instructions verbally, via WhatsApp, or not at all. Task completion is estimated. Problems are reported hours later — if at all.",
    solution: "A role-specific mobile app for every ground handler. Supervisors allocate duties at shift start. Staff update task status in real-time. Incidents are reported with photo evidence instantly.",
    steps: [
      { n: "01", title: "Supervisor allocates duties", body: "At shift start, supervisor assigns each staff member their flights and task responsibilities in 2 minutes." },
      { n: "02", title: "Staff executes on mobile", body: "Each staff member sees only their tasks. One tap to start, complete, delay or flag. No training needed." },
      { n: "03", title: "Live status flows upward", body: "Every update appears instantly on the supervisor's Turnaround Control board. Full traceability." },
    ],
    roi: [
      { metric: "100%", label: "Task traceability vs. zero with paper" },
      { metric: "8 min", label: "Shift briefing time (down from 25 min)" },
      { metric: "0", label: "WhatsApp groups needed" },
    ],
    appLink: "/ramp",
    color: "blue",
  },
  {
    slug: "delay-prediction",
    name: "AI Delay Prediction Engine",
    brand: "GroundShield",
    icon: "Brain",
    badge: "AI POWERED",
    tagline: "Know 20 minutes before the captain that you're going to be late.",
    problem: "Departure delays are declared reactively. By the time ops control knows, the passenger is already at the gate. Cascading rotational delays cost $4,200 per hour on average.",
    solution: "An AI engine (Claude Sonnet) analyses task completion velocity, vendor SLA history and real-time weather to generate a departure confidence score — updated every 2 minutes.",
    steps: [
      { n: "01", title: "Task velocity analysis", body: "AI monitors how fast each turnaround task is progressing relative to the required completion time." },
      { n: "02", title: "Historical SLA scoring", body: "Vendor performance history, weather impact and fleet type factors are weighted against current conditions." },
      { n: "03", title: "Confidence score + action", body: "A 0–100 confidence score with specific intervention recommendations is pushed to the supervisor instantly." },
    ],
    roi: [
      { metric: "22 min", label: "Earlier delay warning vs. industry average" },
      { metric: "67%", label: "Reduction in cascading delay events" },
      { metric: "$340K", label: "Annual savings for 50-rotation airline" },
    ],
    appLink: "/turnaround/board",
    color: "purple",
  },
  {
    slug: "vendor-sla",
    name: "Vendor SLA Manager",
    brand: "GroundShield",
    icon: "Shield",
    badge: "ACCOUNTABILITY",
    tagline: "Stop paying vendors for performance you cannot measure.",
    problem: "Ground handling contracts are renewed based on relationship, not data. SLA breaches go untracked. Vendors over-promise, under-deliver and invoice correctly regardless.",
    solution: "Every vendor task is timestamped and scored automatically. SLA compliance dashboards, breach alerts and exportable performance reports give airlines the data to negotiate from strength.",
    steps: [
      { n: "01", title: "Automatic task timestamps", body: "Every vendor task start and completion is logged automatically via ramp staff mobile updates — no manual entry." },
      { n: "02", title: "SLA compliance scoring", body: "Each vendor receives a daily, weekly and monthly compliance score vs. contracted SLA targets." },
      { n: "03", title: "Breach report generation", body: "Export breach summaries with timestamps and flight references for contract review meetings." },
    ],
    roi: [
      { metric: "100%", label: "Vendor activity visibility" },
      { metric: "18%", label: "Average handling cost reduction after 6 months" },
      { metric: "4 hrs", label: "Saved per week on manual SLA reporting" },
    ],
    appLink: "/turnaround/vendors",
    color: "amber",
  },
  {
    slug: "staff-planning",
    name: "Ground Staff Planning",
    brand: "GroundShield",
    icon: "Users",
    badge: "SHIFT CONTROL",
    tagline: "Allocate every person to every flight in 5 minutes.",
    problem: "Ground supervisors start each shift knowing who is present but not who does what for which flight. Coverage gaps only become visible when someone doesn't show up at the aircraft.",
    solution: "A 3-phase shift planning tool: Allocate staff to flights at shift start → Monitor live task progress → Review shift performance report at end of duty.",
    steps: [
      { n: "01", title: "Allocate (shift start)", body: "Supervisor sees all staff on shift + all flights. One tap assigns a staff member to all their role-tasks on a specific flight." },
      { n: "02", title: "Monitor (active shift)", body: "Live grid shows each staff member's task completion progress, active tasks and any delays in real-time." },
      { n: "03", title: "Report (end of shift)", body: "Automatic shift performance summary: completion rates, on-time scores, incidents and staff KPIs." },
    ],
    roi: [
      { metric: "5 min", label: "Complete shift allocation time" },
      { metric: "0", label: "Coverage gaps missed with structured allocation" },
      { metric: "1 screen", label: "Full shift visibility for supervisor" },
    ],
    appLink: "/turnaround/staff-planning",
    color: "teal",
  },
  {
    slug: "ground-fatigue",
    name: "Ground Fatigue Shield",
    brand: "GroundShield",
    icon: "Activity",
    badge: "SAFETY",
    tagline: "A fatigued ramp agent is a safety incident waiting to happen.",
    problem: "Ground staff fatigue is untracked and unmanaged at most airlines. Excessive consecutive days, insufficient rest and overtime are invisible risks — until an aircraft is damaged.",
    solution: "Continuous fatigue risk scoring for every ground staff member based on duty hours, rest periods and consecutive days — with regulatory threshold enforcement and automatic alerts.",
    steps: [
      { n: "01", title: "Duty hour tracking", body: "Each staff member's duty hours are tracked automatically from shift start/complete events." },
      { n: "02", title: "Risk score calculation", body: "A 0–100 fatigue score is computed using 24h duty, 7-day total, rest hours and consecutive days." },
      { n: "03", title: "Automated intervention alerts", body: "High/critical risk staff are flagged automatically. Supervisor is notified before the shift assignment is made." },
    ],
    roi: [
      { metric: "83%", label: "Ramp incidents linked to fatigue (IATA data)" },
      { metric: "100%", label: "Regulatory compliance visibility" },
      { metric: "Zero", label: "Surprises on duty-hour violations" },
    ],
    appLink: "/turnaround/ground-fatigue",
    color: "red",
  },
  {
    slug: "crew-fatigue",
    name: "Crew Rostering & FRMS",
    brand: "Fatigue Shield",
    icon: "PilotIcon",
    badge: "DGCA COMPLIANT",
    tagline: "The only crew planning tool built around fatigue science.",
    problem: "Legacy crew planning tools optimise for cost and coverage. Fatigue risk is a checkbox — not a constraint. DGCA violations are caught during audit, not before a flight.",
    solution: "An AI-powered crew rostering, pairing builder and FRMS that treats fatigue as the primary planning constraint. Every pairing is DGCA-compliant before it's published.",
    steps: [
      { n: "01", title: "Fatigue-first pairing", body: "The pairing builder flags duty-hour violations, rest violations and fatigue risk in real-time as you schedule." },
      { n: "02", title: "Monthly roster with FRMS", body: "Monthly rosters are generated with full FRMS fatigue risk modelling across the crew base." },
      { n: "03", title: "AI disruption handling", body: "When IROPS hit, the recovery console finds compliant crew replacements instantly — no manual fatigue checking." },
    ],
    roi: [
      { metric: "0", label: "DGCA violations in compliant rosters" },
      { metric: "40%", label: "Reduction in last-minute crew changes" },
      { metric: "6 hrs", label: "Saved per week on roster management" },
    ],
    appLink: "/crew/roster",
    color: "indigo",
  },
];

export const MODULE_COLOR = {
  orange: { bg: "bg-orange-500/10", border: "border-orange-500/25", text: "text-orange-400", glow: "hover:shadow-orange-500/15" },
  blue:   { bg: "bg-blue-500/10",   border: "border-blue-500/25",   text: "text-blue-400",   glow: "hover:shadow-blue-500/15"   },
  purple: { bg: "bg-purple-500/10", border: "border-purple-500/25", text: "text-purple-400", glow: "hover:shadow-purple-500/15" },
  amber:  { bg: "bg-amber-500/10",  border: "border-amber-500/25",  text: "text-amber-400",  glow: "hover:shadow-amber-500/15"  },
  teal:   { bg: "bg-teal-500/10",   border: "border-teal-500/25",   text: "text-teal-400",   glow: "hover:shadow-teal-500/15"   },
  red:    { bg: "bg-red-500/10",    border: "border-red-500/30",    text: "text-red-400",    glow: "hover:shadow-red-500/15"    },
  indigo: { bg: "bg-indigo-500/10", border: "border-indigo-500/25", text: "text-indigo-400", glow: "hover:shadow-indigo-500/15" },
};
